import { useState, useEffect, useMemo, useRef } from 'react';
import { STORAGE_KEYS } from '../helpers/constants';
import channelService from '../services/channelService';

// Cache entries expire after 5 minutes so a role change mid-session is reflected
// within one TTL window without requiring a full page reload.
const ADMIN_CACHE_TTL_MS = 5 * 60 * 1000;

interface AdminCacheEntry {
  value: boolean;
  expiresAt: number;
}

// Module-level cache: channelId → isAdmin result with TTL.
// Survives re-renders and menu open/close cycles; cleared on page reload.
const adminCache = new Map<string, AdminCacheEntry>();

const getFromCache = (channelId: string): boolean | undefined => {
  const entry = adminCache.get(channelId);
  if (!entry) return undefined;
  if (Date.now() >= entry.expiresAt) {
    adminCache.delete(channelId);
    return undefined;
  }
  return entry.value;
};

// Shared fetch logic used by both the hook and prefetchChannelAdmin
const fetchAndCacheAdmin = async (channelId: string): Promise<boolean> => {
  const cached = getFromCache(channelId);
  if (cached !== undefined) return cached;

  try {
    const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
    if (!storedUserData) {
      adminCache.set(channelId, { value: false, expiresAt: Date.now() + ADMIN_CACHE_TTL_MS });
      return false;
    }

    const parsedUserData = JSON.parse(storedUserData);
    const currentUserId = parsedUserData.user_id;

    const members = await channelService.getChannelMembersNoPaging(channelId);
    const currentUserMember = members.find(
      (member) => member.id === currentUserId || member.email === parsedUserData.email_id
    );

    const isAdmin =
      currentUserMember?.role?.toLowerCase() === 'admin' ||
      currentUserMember?.role === 'Admin';

    adminCache.set(channelId, { value: isAdmin, expiresAt: Date.now() + ADMIN_CACHE_TTL_MS });
    return isAdmin;
  } catch (error) {
    console.error('Error checking channel admin status:', error);
    adminCache.delete(channelId);
    return false;
  }
};

/**
 * Fire-and-forget prefetch: call this on mouseenter of the three-dots button so
 * the result is cached before the user clicks, making the menu open instantly.
 */
export const prefetchChannelAdmin = (channelId: string): void => {
  if (!channelId || getFromCache(channelId) !== undefined) return;
  fetchAndCacheAdmin(channelId);
};

/**
 * Drop the cached admin status for a channel.
 * Call after any operation that may have changed a member's role (e.g. members update)
 * so the next menu open re-fetches fresh data instead of serving a stale result.
 */
export const invalidateAdminCache = (channelId: string): void => {
  adminCache.delete(channelId);
};

export const useChannelAdmin = (channelId?: string) => {
  // `cached` seeds the useState initializers at first mount only — useState ignores it on
  // subsequent renders. For channelId changes after mount, useEffect([channelId]) below
  // is what actually updates isChannelAdmin and loading. useMemo here avoids an extra
  // getFromCache() call at mount when channelId is already provided and cached.
  const cached = useMemo(
    () => (channelId !== undefined ? getFromCache(channelId) : undefined),
    [channelId],
  );

  const [isChannelAdmin, setIsChannelAdmin] = useState<boolean>(cached ?? false);
  const [loading, setLoading] = useState<boolean>(cached === undefined && !!channelId);

  // Tracks which channelId the current state was last resolved for.
  // On re-renders before the effect runs (when channelId changes), this ref lags behind,
  // making effectiveLoading synchronously true and preventing the menu from opening with
  // stale isChannelAdmin from the previous channel.
  const resolvedChannelRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!channelId) {
      resolvedChannelRef.current = channelId;
      setIsChannelAdmin(false);
      setLoading(false);
      return;
    }

    // Cache hit — resolve instantly without an API call
    const cachedValue = getFromCache(channelId);
    if (cachedValue !== undefined) {
      resolvedChannelRef.current = channelId;
      setIsChannelAdmin(cachedValue);
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchAndCacheAdmin(channelId).then((isAdmin) => {
      resolvedChannelRef.current = channelId;
      setIsChannelAdmin(isAdmin);
      setLoading(false);
    });
  }, [channelId]);

  // Synchronously true when channelId changes before the effect has resolved it,
  // closing the stale-loading window that caused Settings to flicker.
  const effectiveLoading = loading || resolvedChannelRef.current !== channelId;

  return { isChannelAdmin, loading: effectiveLoading };
};
